import Image from "@tiptap/extension-image";

export type StorageImageOptions = {
  resolveArtifactUrl?: (artifactId: string) => Promise<string | null>;
  [key: string]: unknown;
};

/**
 * Image node with a persisted artifact reference.
 *
 * `storageFileId` is retained as the serialized Tiptap attribute name, but its value is the
 * canonical artifact id. URL refreshes always re-authorize that artifact placement.
 */
export const StorageImage = Image.extend<StorageImageOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      resolveArtifactUrl: undefined,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      storageFileId: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-storage-file-id"),
        renderHTML: (attributes) =>
          attributes.storageFileId ? { "data-storage-file-id": attributes.storageFileId } : {},
      },
      _diffStatus: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-diff-status"),
        renderHTML: (attributes) =>
          attributes._diffStatus ? { "data-diff-status": attributes._diffStatus } : {},
      },
    };
  },

  addNodeView() {
    return ({ node, HTMLAttributes }) => {
      const frame = document.createElement("figure");
      const image = document.createElement("img");
      let destroyed = false;
      let currentNode = node;
      let lastFailedSource: string | null = null;

      const applyAttributes = () => {
        frame.className = "editor-image-frame";
        for (const [key, value] of Object.entries(HTMLAttributes)) {
          if (value === undefined || value === null || value === false) {
            image.removeAttribute(key);
            continue;
          }
          image.setAttribute(key, String(value));
        }
        if (typeof currentNode.attrs.alt === "string") {
          image.setAttribute("alt", currentNode.attrs.alt);
        }
        if (typeof currentNode.attrs.title === "string") {
          image.setAttribute("title", currentNode.attrs.title);
        }
        if (typeof currentNode.attrs.storageFileId === "string") {
          image.setAttribute("data-storage-file-id", currentNode.attrs.storageFileId);
        }
        const diffStatus =
          typeof currentNode.attrs._diffStatus === "string" ? currentNode.attrs._diffStatus : null;
        if (diffStatus) {
          frame.setAttribute("data-diff-status", diffStatus);
          image.setAttribute("data-diff-status", diffStatus);
        } else {
          frame.removeAttribute("data-diff-status");
          image.removeAttribute("data-diff-status");
        }
      };

      const resolveSource = () => {
        if (typeof currentNode.attrs.src === "string" && currentNode.attrs.src) {
          image.setAttribute("src", currentNode.attrs.src);
          return;
        }
        const artifactId =
          typeof currentNode.attrs.storageFileId === "string"
            ? currentNode.attrs.storageFileId
            : null;
        if (!artifactId || !this.options.resolveArtifactUrl) {
          image.removeAttribute("src");
          return;
        }
        this.options
          .resolveArtifactUrl(artifactId)
          .then((url) => {
            if (!destroyed && url) {
              image.setAttribute("src", url);
            }
          })
          .catch(() => {
            if (!destroyed) {
              image.removeAttribute("src");
            }
          });
      };

      image.addEventListener("error", () => {
        const artifactId =
          typeof currentNode.attrs.storageFileId === "string"
            ? currentNode.attrs.storageFileId
            : null;
        const failedSource = image.getAttribute("src");
        if (
          !artifactId ||
          !failedSource ||
          failedSource === lastFailedSource ||
          !this.options.resolveArtifactUrl
        ) {
          return;
        }
        lastFailedSource = failedSource;
        this.options
          .resolveArtifactUrl(artifactId)
          .then((url) => {
            if (!destroyed && url && url !== failedSource) {
              image.setAttribute("src", url);
            }
          })
          .catch(() => {
            if (!destroyed) {
              image.removeAttribute("src");
            }
          });
      });

      applyAttributes();
      resolveSource();
      frame.appendChild(image);

      return {
        dom: frame,
        update: (nextNode) => {
          if (nextNode.type !== currentNode.type) {
            return false;
          }
          currentNode = nextNode;
          applyAttributes();
          resolveSource();
          return true;
        },
        destroy: () => {
          destroyed = true;
        },
      };
    };
  },
});
